/**
 * 🎉 FLYER ANNIVERSAIRE - 1 AN DE LA PETITE ÉPICERIE
 * Grand jeu concours avec panier mystère à gagner !
 * Affiché jusqu'au 5 avril 2025
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, Share2, ExternalLink } from 'lucide-react';

const FLYER_IMAGE = "https://customer-assets.emergentagent.com/job_0ffbf8f7-9d61-4a4f-ab60-c014ce5c7a2d/artifacts/teorh5ka_0.jpg";
const SNAPCHAT_URL = "https://www.snapchat.com/@lapetiteepi62";
const CONTEST_END_DATE = new Date('2026-04-05T23:59:59');

export default function AnniversaryFlyer({ variant = 'banner' }) {
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  // Vérifier si le concours est toujours actif
  const isContestActive = new Date() < CONTEST_END_DATE;
  
  if (!isContestActive || dismissed) return null;

  // Variante BANNER - Barre en haut de page ou section
  if (variant === 'banner') {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden cursor-pointer group"
          onClick={() => setShowModal(true)}
          style={{
            background: 'linear-gradient(135deg, #8B0000 0%, #DC143C 50%, #8B0000 100%)',
          }}
        >
          {/* Confetti animation */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full"
                style={{
                  background: ['#FFD700', '#FFA500', '#FFFFFF', '#FF69B4'][i % 4],
                  left: `${Math.random() * 100}%`,
                  top: `-10px`,
                }}
                animate={{
                  y: ['0vh', '100vh'],
                  rotate: [0, 360],
                  opacity: [1, 0],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                  ease: 'linear',
                }}
              />
            ))}
          </div>

          <div className="relative z-10 py-3 px-4">
            <div className="max-w-4xl mx-auto flex items-center justify-center gap-4 flex-wrap">
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.6, repeat: Infinity }}
                className="text-2xl"
              >
                🎉
              </motion.span>
              
              <div className="text-center">
                <p className="text-white font-bold text-sm md:text-base">
                  🎂 1 AN DE LA PETITE ÉPICERIE ! Grand Jeu Anniversaire 🎁
                </p>
                <p className="text-yellow-300 text-xs md:text-sm font-semibold">
                  Gagne un PANIER MYSTÈRE (+50€) → Clique pour participer !
                </p>
              </div>
              
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: 0.3 }}
                className="text-2xl"
              >
                🎁
              </motion.span>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-4 py-1.5 bg-yellow-400 text-red-900 font-bold text-sm rounded-full shadow-lg"
              >
                PARTICIPER
              </motion.button>
            </div>
          </div>
          
          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDismissed(true);
            }}
            className="absolute top-2 right-2 p-1 text-white/60 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>

        {/* Modal avec le flyer */}
        <FlyerModal isOpen={showModal} onClose={() => setShowModal(false)} />
      </>
    );
  }

  // Variante CARD - Pour affichage dans une section
  if (variant === 'card') {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.02 }}
          className="relative overflow-hidden rounded-2xl cursor-pointer group"
          onClick={() => setShowModal(true)}
          style={{
            background: 'linear-gradient(135deg, #1a0608 0%, #2d0f1a 100%)',
            border: '3px solid',
            borderImage: 'linear-gradient(135deg, #d4af37, #8b6914, #d4af37) 1',
          }}
        >
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 via-red-500/10 to-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="relative z-10 p-6">
            <div className="flex items-center gap-4">
              {/* Mini preview du flyer */}
              <div className="w-24 h-32 rounded-lg overflow-hidden shadow-lg flex-shrink-0 border-2 border-yellow-500/30">
                <img 
                  src={FLYER_IMAGE} 
                  alt="Flyer 1 an" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <motion.span
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="text-2xl"
                  >
                    🎂
                  </motion.span>
                  <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
                    1 AN !
                  </h3>
                </div>
                
                <p className="text-white font-semibold mb-1">Grand Jeu Anniversaire</p>
                <p className="text-gray-400 text-sm mb-3">Gagne un panier mystère (+50€) !</p>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold text-sm rounded-full shadow-lg flex items-center gap-2"
                >
                  <Gift className="w-4 h-4" />
                  PARTICIPER
                </motion.button>
              </div>
            </div>
            
            {/* Countdown */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <ContestCountdown endDate={CONTEST_END_DATE} />
            </div>
          </div>
        </motion.div>

        <FlyerModal isOpen={showModal} onClose={() => setShowModal(false)} />
      </>
    );
  }

  return null;
}

// Modal plein écran avec le flyer
function FlyerModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto py-4"
        style={{ background: 'rgba(0,0,0,0.95)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="relative w-full max-w-lg mx-4 my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button - Fixed position */}
          <button
            onClick={onClose}
            className="fixed top-4 right-4 z-20 p-3 bg-black/70 rounded-full text-white hover:bg-black transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Flyer image - Full size */}
          <img
            src={FLYER_IMAGE}
            alt="1 AN DE LA PETITE ÉPICERIE - Grand Jeu Anniversaire"
            className="w-full h-auto rounded-2xl shadow-2xl"
          />
          
          {/* Action buttons - Below the image */}
          <div className="mt-4 p-4 bg-black/80 rounded-2xl backdrop-blur-sm">
            <div className="flex gap-3">
              <motion.a
                href={SNAPCHAT_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 py-4 bg-yellow-400 text-black font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg text-lg"
              >
                <span className="text-2xl">👻</span>
                Ajouter sur Snap
              </motion.a>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: '🎂 1 AN - La Petite Épicerie',
                      text: 'Grand Jeu Anniversaire ! Gagne un panier mystère ! 🎁',
                      url: window.location.href,
                    });
                  }
                }}
                className="p-4 bg-white/20 text-white rounded-xl"
              >
                <Share2 className="w-6 h-6" />
              </motion.button>
            </div>
            
            <p className="text-center text-gray-400 text-sm mt-3">
              Fin des participations : 4 Avril à 23h59
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Compte à rebours
function ContestCountdown({ endDate }) {
  const [timeLeft, setTimeLeft] = React.useState(getTimeLeft(endDate));
  
  React.useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeLeft(endDate));
    }, 1000);
    return () => clearInterval(timer);
  }, [endDate]);
  
  function getTimeLeft(end) {
    const diff = end - new Date();
    if (diff <= 0) return null;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return { days, hours, minutes };
  }
  
  if (!timeLeft) return <p className="text-red-400 text-sm text-center">Concours terminé !</p>;
  
  return (
    <div className="flex items-center justify-center gap-4 text-center">
      <span className="text-gray-400 text-xs">Fin dans :</span>
      <div className="flex gap-2">
        <div className="px-2 py-1 bg-red-900/50 rounded text-white text-sm font-mono">
          {timeLeft.days}j
        </div>
        <div className="px-2 py-1 bg-red-900/50 rounded text-white text-sm font-mono">
          {timeLeft.hours}h
        </div>
        <div className="px-2 py-1 bg-red-900/50 rounded text-white text-sm font-mono">
          {timeLeft.minutes}m
        </div>
      </div>
    </div>
  );
}

export { FlyerModal, ContestCountdown };
